import { withFormik, FormikProps, FieldArray, ErrorMessage, Field } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import classnames from 'classnames';
import BixButton from '$/components/global/bixButton';
import BixCheckbox from '$/components/global/bixCheckbox';
import InputBlock from '$/components/global/inputBlock';
import Modal from '$/components/global/modal';
import { ReactComponent as Chevron } from '$/assets/icons/Chevron.svg';
import * as Bixby from '$/core';
import './CompanyDeclarationFormBuilder.scss';

export interface CompanyDeclarationFormBuilderValues {
  introduction: string;
  questions: Bixby.Models.CompanyDeclarationFormQuestion[];
}

interface ICompanyDeclarationFormBuilderFormikProps {
  initialValues: CompanyDeclarationFormBuilderValues;
  isDefault: boolean;
  onSubmit: (values: CompanyDeclarationFormBuilderValues) => Promise<void>;
}

const CompanyDeclarationFormBuilderFormik: React.FC<
  ICompanyDeclarationFormBuilderFormikProps & FormikProps<CompanyDeclarationFormBuilderValues>
> = props => {
  const {
    errors,
    touched,
    handleChange,
    handleBlur,
    values,
    handleSubmit,
    isSubmitting,
    isValid,
    status,
    isDefault,
    setFieldValue,
  } = props;

  const [showConfirmModal, setShowConfirmModal] = useState(false as boolean);
  const [expandedQuestionIndices, setExpandedQuestionIndices] = useState([] as number[]);

  const handleCloseModal = () => {
    setShowConfirmModal(false);
  };

  const handleSubmitConfirmation = () => {
    setShowConfirmModal(true);
  };

  return (
    <form onSubmit={handleSubmit} className="company_declaration_form_builder">
      <h2>{`Company Declaration Form Builder${isDefault ? ' (Using Default Form)' : ''}`}</h2>
      <div>
        <InputBlock hasError={!!errors.introduction && !!touched.introduction} title="Introduction">
          <textarea
            onChange={handleChange}
            onBlur={handleBlur}
            id="introduction"
            name="introduction"
            value={values.introduction}
          />
          <ErrorMessage name="introduction" component="p" className="error" />
        </InputBlock>
        <hr />
        <h2>Questions</h2>
        <FieldArray
          name="questions"
          render={questionArrayHelper => (
            <div>
              {values.questions && values.questions.length > 0 ? (
                values.questions.map((question, questionIndex) => {
                  return (
                    <div key={questionIndex} className="question flexbox">
                      <div>
                        <h2
                          onClick={() => {
                            const newArray: number[] = expandedQuestionIndices.slice();
                            if (newArray.indexOf(questionIndex) === -1) {
                              newArray.push(questionIndex);
                            } else {
                              const index = newArray.indexOf(questionIndex);
                              if (index > -1) {
                                newArray.splice(index, 1);
                              }
                            }
                            setExpandedQuestionIndices(newArray);
                          }}
                          className={classnames({ expanded: expandedQuestionIndices.indexOf(questionIndex) !== -1 })}
                          role="button">
                          <span>{`Question ${question.order}`}</span>
                          <Chevron
                            className={classnames('expand', {
                              expanded: expandedQuestionIndices.indexOf(questionIndex) !== -1,
                            })}
                          />
                          <span className="title_preview">{question.title}</span>
                        </h2>
                        <div
                          className={`question-content${
                            expandedQuestionIndices.indexOf(questionIndex) !== -1 ? ' expanded' : ''
                          }`}>
                          <div>
                            <InputBlock title="Question Type" required>
                              <select
                                id={`questions.${questionIndex}.questionType`}
                                name={`questions.${questionIndex}.questionType`}
                                value={question.questionType}
                                onChange={e => {
                                  if (
                                    question.questionType == Bixby.Enums.QuestionType.Checkbox ||
                                    question.questionType == Bixby.Enums.QuestionType.Dropdown ||
                                    question.questionType == Bixby.Enums.QuestionType.Radio
                                  ) {
                                    question.responseOptions = [];
                                    question.validationRule.requireSpecificResponse = false;
                                  }
                                  handleChange(e);
                                }}
                                onBlur={handleBlur}>
                                <option key={-1} value="" disabled>
                                  Select Question Type
                                </option>
                                {Object.keys(Bixby.Enums.QuestionType)
                                  .filter(v => isNaN(parseInt(v)))
                                  .map(type => {
                                    return (
                                      <option key={`question-type-${type}`} value={type}>
                                        {type}
                                      </option>
                                    );
                                  })}
                              </select>
                            </InputBlock>
                            <InputBlock title="Title" required>
                              <input
                                onChange={handleChange}
                                onBlur={handleBlur}
                                id={`questions.${questionIndex}.title`}
                                name={`questions.${questionIndex}.title`}
                                value={question.title}
                              />
                              <ErrorMessage name={`questions.${questionIndex}.title`} component="p" className="error" />
                            </InputBlock>
                            {(question.questionType == Bixby.Enums.QuestionType.Checkbox ||
                              question.questionType == Bixby.Enums.QuestionType.Dropdown ||
                              question.questionType == Bixby.Enums.QuestionType.Radio) && (
                              <>
                                <h3>Response Options</h3>
                                <FieldArray
                                  name={`questions.${questionIndex}.responseOptions`}
                                  render={responseOptionArrayHelper => (
                                    <div className="question_response">
                                      {question.responseOptions && question.responseOptions.length > 0 ? (
                                        question.responseOptions.map((responseOption, responseOptionIndex) => {
                                          return (
                                            <div key={responseOptionIndex} className="flexbox">
                                              <InputBlock useSmall title="Value" required>
                                                <input
                                                  onChange={handleChange}
                                                  onBlur={handleBlur}
                                                  id={`questions.${questionIndex}.responseOptions.${responseOptionIndex}.value`}
                                                  name={`questions.${questionIndex}.responseOptions.${responseOptionIndex}.value`}
                                                  value={responseOption.value}
                                                />
                                                <ErrorMessage
                                                  name={`questions.${questionIndex}.responseOptions.${responseOptionIndex}.value`}
                                                  component="p"
                                                  className="error"
                                                />
                                              </InputBlock>
                                              <div className="actions_wrap">
                                                <div className="remove_wrap">
                                                  <button
                                                    type="button"
                                                    className="remove_btn"
                                                    onClick={() =>
                                                      responseOptionArrayHelper.remove(responseOptionIndex)
                                                    }>
                                                    <span className="fa fa-minus-circle"></span>Remove
                                                  </button>
                                                </div>
                                                <div className="add_wrap">
                                                  <button
                                                    type="button"
                                                    className="add_btn"
                                                    onClick={() => {
                                                      for (let i = 0; i < question.responseOptions.length; i++) {
                                                        if (i > responseOptionIndex) {
                                                          question.responseOptions[i].order += 1;
                                                        }
                                                      }
                                                      responseOptionArrayHelper.insert(responseOptionIndex + 1, {
                                                        id: uuidv4(),
                                                        value: '{pending}',
                                                        order: responseOptionIndex + 2,
                                                      } as Bixby.Models.CompanyDeclarationFormQuestionResponseOption);
                                                    }}>
                                                    + Add
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <BixButton
                                          type="button"
                                          onClick={() =>
                                            responseOptionArrayHelper.push({
                                              id: uuidv4(),
                                              value: '{pending}',
                                              order: 1,
                                            } as Bixby.Models.CompanyDeclarationFormQuestionResponseOption)
                                          }>
                                          Add Response Option
                                        </BixButton>
                                      )}
                                    </div>
                                  )}
                                />
                              </>
                            )}
                            <h3>Validation Logic</h3>
                            <div className="validation_rule flexbox">
                              <div>
                                <Field name={`questions.${questionIndex}.validationRule.isRequired`}>
                                  {({ field }) => (
                                    <BixCheckbox
                                      id={`questions.${questionIndex}.validationRule.isRequired`}
                                      checked={field.value}
                                      onChange={field.onChange(field.name)}>
                                      Is Required
                                    </BixCheckbox>
                                  )}
                                </Field>
                              </div>
                              {(question.questionType == Bixby.Enums.QuestionType.Checkbox ||
                                question.questionType == Bixby.Enums.QuestionType.Dropdown ||
                                question.questionType == Bixby.Enums.QuestionType.Radio) && (
                                <div>
                                  <Field name={`questions.${questionIndex}.validationRule.requireSpecificResponse`}>
                                    {({ field }) => (
                                      <BixCheckbox
                                        id={`questions.${questionIndex}.validationRule.requireSpecificResponse`}
                                        checked={field.value}
                                        onChange={field.onChange(field.name)}>
                                        Requires Specific Response
                                      </BixCheckbox>
                                    )}
                                  </Field>
                                  {(question.questionType == Bixby.Enums.QuestionType.Checkbox ||
                                    question.questionType == Bixby.Enums.QuestionType.Dropdown ||
                                    question.questionType == Bixby.Enums.QuestionType.Radio) &&
                                    question.validationRule.requireSpecificResponse && (
                                      <>
                                        <InputBlock useSmall title="Required Response Value" required>
                                          <select
                                            id={`questions.${questionIndex}.validationRule.requireSpecificResponseValue_Id`}
                                            name={`questions.${questionIndex}.validationRule.requireSpecificResponseValue_Id`}
                                            value={question.validationRule.requireSpecificResponseValue_Id || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}>
                                            <option key={-1} value="" disabled>
                                              Select Response
                                            </option>
                                            {question.responseOptions.map(responseOption => {
                                              return (
                                                <option
                                                  key={`specific-response-option-${responseOption.id}`}
                                                  value={responseOption.id}>
                                                  {responseOption.value}
                                                </option>
                                              );
                                            })}
                                          </select>
                                        </InputBlock>
                                      </>
                                    )}
                                </div>
                              )}
                            </div>
                            <h3>Conditional Logic</h3>
                            {question.conditionalLogic ? (
                              <div key={questionIndex} className="conditional_logic flexbox">
                                <div>
                                  <InputBlock useSmall title="Depends on Question" required>
                                    <select
                                      id={`questions.${questionIndex}.conditionalLogic.dependsOnQuestion_Id`}
                                      name={`questions.${questionIndex}.conditionalLogic.dependsOnQuestion_Id`}
                                      value={question.conditionalLogic.dependsOnQuestion_Id}
                                      onChange={handleChange}
                                      onBlur={handleBlur}>
                                      <option key={-1} value="" disabled>
                                        Select Question
                                      </option>
                                      {values.questions
                                        .filter(
                                          _ =>
                                            _.id !== question.id &&
                                            _.questionType !== Bixby.Enums.QuestionType.File &&
                                            _.order < question.order
                                        )
                                        .map(dependsOnQuestionOption => {
                                          return (
                                            <option
                                              key={`depends-on-question-${dependsOnQuestionOption.id}`}
                                              value={dependsOnQuestionOption.id}>
                                              {dependsOnQuestionOption.title}
                                            </option>
                                          );
                                        })}
                                    </select>
                                  </InputBlock>
                                  <InputBlock useSmall title="Depends on Question Value" required>
                                    <select
                                      id={`questions.${questionIndex}.conditionalLogic.dependsOnQuestionResponseValue_Id`}
                                      name={`questions.${questionIndex}.conditionalLogic.dependsOnQuestionResponseValue_Id`}
                                      value={question.conditionalLogic.dependsOnQuestionResponseValue_Id}
                                      disabled={
                                        !question.conditionalLogic.dependsOnQuestion_Id ||
                                        !question.conditionalLogic ||
                                        values.questions.filter(
                                          _ =>
                                            question.conditionalLogic !== null &&
                                            _.id === question.conditionalLogic.dependsOnQuestion_Id
                                        ).length === 0 ||
                                        values.questions.filter(
                                          _ =>
                                            question.conditionalLogic !== null &&
                                            _.id === question.conditionalLogic.dependsOnQuestion_Id
                                        )[0].responseOptions.length === 0
                                      }
                                      onChange={handleChange}
                                      onBlur={handleBlur}>
                                      <option key={-1} value="" disabled>
                                        Select Question Value
                                      </option>
                                      {(
                                        values.questions.filter(
                                          _ => _.id === question.conditionalLogic?.dependsOnQuestion_Id
                                        )[0].responseOptions || []
                                      ).map(dependsOnQuestionValue => {
                                        return (
                                          <option
                                            key={`depends-on-question-value-${dependsOnQuestionValue.id}`}
                                            value={dependsOnQuestionValue.id}>
                                            {dependsOnQuestionValue.value}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </InputBlock>
                                  <InputBlock useSmall title="Action" required>
                                    <select
                                      id={`questions.${questionIndex}.conditionalLogic.action`}
                                      name={`questions.${questionIndex}.conditionalLogic.action`}
                                      value={question.conditionalLogic.action}
                                      onChange={handleChange}
                                      onBlur={handleBlur}>
                                      <option key={-1} value="" disabled>
                                        Select Action
                                      </option>
                                      {Object.keys(Bixby.Enums.QuestionConditionalAction)
                                        .filter(v => isNaN(parseInt(v)))
                                        .map(type => {
                                          return (
                                            <option key={`question-action-${type}`} value={type}>
                                              {type}
                                            </option>
                                          );
                                        })}
                                    </select>
                                  </InputBlock>
                                </div>
                                <div>
                                  <div className="actions_wrap">
                                    <div className="remove_wrap">
                                      <button
                                        type="button"
                                        className="remove_btn"
                                        onClick={() => {
                                          setFieldValue(
                                            `questions.${questionIndex}.conditionalLogic`,
                                            null as Bixby.Models.CompanyDeclarationFormQuestionConditionalLogic | null
                                          );
                                        }}>
                                        <span className="fa fa-minus-circle"></span>Remove
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <BixButton
                                type="button"
                                disabled={values.questions.length < 2 || questionIndex === 0}
                                onClick={() => {
                                  const firstAvailableQuestion = values.questions.filter(_ => _.id !== question.id)[0];
                                  const conditionalLogic: Bixby.Models.CompanyDeclarationFormQuestionConditionalLogic =
                                    {
                                      id: uuidv4(),
                                      dependsOnQuestion_Id: firstAvailableQuestion.id,
                                      dependsOnQuestionResponseValue_Id: '',
                                      action: Bixby.Enums.QuestionConditionalAction.Show,
                                    };
                                  setFieldValue(`questions.${questionIndex}.conditionalLogic`, conditionalLogic);
                                }}>
                                Add Conditional Logic
                              </BixButton>
                            )}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="actions_wrap">
                          <div className="remove_wrap">
                            <button
                              type="button"
                              className="remove_btn"
                              onClick={() => {
                                const idToRemove = values.questions[questionIndex].id;
                                // Iterate over questions and check if any conditional logic depends on this question being removed
                                for (let i = 0; i < values.questions.length; i++) {
                                  const currentQuestion: Bixby.Models.CompanyDeclarationFormQuestion =
                                    values.questions[i];
                                  if (currentQuestion.conditionalLogic != null) {
                                    if (currentQuestion.conditionalLogic.dependsOnQuestion_Id === idToRemove) {
                                      // If so, remove the conditional logic for this question
                                      const currentQuestionIndex: number = values.questions.findIndex(
                                        element => element.id === currentQuestion.id
                                      );
                                      setFieldValue(`questions.${currentQuestionIndex}.conditionalLogic`, null);
                                    }
                                  }
                                }
                                questionArrayHelper.remove(questionIndex);
                              }}>
                              <span className="fa fa-minus-circle"></span>Remove
                            </button>
                          </div>
                          <div className="add_wrap">
                            <button
                              type="button"
                              className="add_btn"
                              onClick={() => {
                                for (let i = 0; i < values.questions.length; i++) {
                                  if (i > questionIndex) {
                                    values.questions[i].order += 1;
                                  }
                                }
                                questionArrayHelper.insert(questionIndex + 1, {
                                  id: uuidv4(),
                                  title: '{pending}',
                                  description: null,
                                  order: questionIndex + 2,
                                  questionType: Bixby.Enums.QuestionType.Radio,
                                  responseOptions: [],
                                  validationRule: {
                                    id: uuidv4(),
                                    isRequired: false,
                                    requireSpecificResponse: false,
                                    requireSpecificResponseValue_Id: null,
                                  } as Bixby.Models.CompanyDeclarationFormQuestionValidationRule,
                                  conditionalLogic: null,
                                } as Bixby.Models.CompanyDeclarationFormQuestion);
                              }}>
                              + Add
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <BixButton
                  type="button"
                  onClick={() =>
                    questionArrayHelper.push({
                      id: uuidv4(),
                      title: '{pending}',
                      description: null,
                      order: 1,
                      questionType: Bixby.Enums.QuestionType.Radio,
                      responseOptions: [],
                      validationRule: {
                        id: uuidv4(),
                        isRequired: false,
                        requireSpecificResponse: false,
                        requireSpecificResponseValue_Id: null,
                      } as Bixby.Models.CompanyDeclarationFormQuestionValidationRule,
                      conditionalLogic: null,
                    } as Bixby.Models.CompanyDeclarationFormQuestion)
                  }>
                  Add Question
                </BixButton>
              )}
            </div>
          )}
        />
        <hr />
      </div>
      <div></div>
      <p className="error">{status}</p>
      {isSubmitting && <div>Loading...</div>}
      <div>
        {isValid} {isSubmitting}
      </div>
      <BixButton type="button" disabled={isSubmitting || !isValid} onClick={handleSubmitConfirmation}>
        Save Declaration Form
      </BixButton>
      <Modal
        title="Please confirm creating new company declaration form, which will be used going forward in effect immediately."
        active={showConfirmModal}
        onClose={handleCloseModal}>
        <div className="modal_button_container">
          <BixButton light type="button" onClick={handleCloseModal}>
            Cancel
          </BixButton>
          <BixButton type="submit" disabled={isSubmitting || !isValid} onClick={handleSubmit}>
            Confirm
          </BixButton>
        </div>
      </Modal>
    </form>
  );
};

export default withFormik<ICompanyDeclarationFormBuilderFormikProps, CompanyDeclarationFormBuilderValues>({
  mapPropsToValues: (props: ICompanyDeclarationFormBuilderFormikProps) => {
    return props.initialValues
      ? props.initialValues
      : {
          introduction: '',
          questions: [],
        };
  },

  validationSchema: Yup.object().shape({
    introduction: Yup.string().required('Introduction is required.'),
    questions: Yup.array()
      .min(1, 'At least one question is required.')
      .of(
        Yup.object().shape({
          title: Yup.string().required('Title is required.'),
          questionType: Yup.string().required('Question type is required.'),
          responseOptions: Yup.array().of(
            Yup.object()
              .shape({
                value: Yup.string().required('Response option value is required.'),
              })
              .when('$questionType', {
                is: (questionType: Bixby.Enums.QuestionType) =>
                  questionType === Bixby.Enums.QuestionType.Checkbox ||
                  questionType === Bixby.Enums.QuestionType.Dropdown ||
                  questionType === Bixby.Enums.QuestionType.Radio,
                then: schema => schema.required('At least one response option is required'),
                otherwise: schema => schema.notRequired(),
              })
          ),
          validationRules: Yup.object().shape({
            requireSpecificResponseValue_Id: Yup.string()
              .nullable()
              .when('requireSpecificResponse', {
                is: true,
                then: schema => schema.required('Specific Response Value is required'),
                otherwise: schema => schema.notRequired(),
              }),
          }),
          conditionalLogic: Yup.object()
            .nullable()
            .shape({
              dependsOnQuestion_Id: Yup.string().required('Depends on Question is required.'),
              dependsOnQuestionResponseValue_Id: Yup.string().required('Depends on Question Value is required.'),
              action: Yup.string().required('Action is required.'),
            }),
        })
      ),
  }),

  enableReinitialize: true,

  handleSubmit: async (values: CompanyDeclarationFormBuilderValues, { props, setSubmitting, setStatus }) => {
    setStatus(undefined);

    try {
      setSubmitting(true);
      const updates = { ...values };
      await props.onSubmit(updates);
    } catch (e: any) {
      const message: string = Bixby.API.getErrorMessage(e);
      setStatus(message);
    }

    setSubmitting(false);
  },
})(CompanyDeclarationFormBuilderFormik);
